<template>
  <div
    class="map-canvas noninteractive"
    ref="mapContainer"
    id="map-container"
  />
</template>

<style lang="scss">
.map-canvas {
  height: 100%;
  width: 100%;
  &.noninteractive {
    .leaflet-interactive {
      pointer-events: none;
    }
  }
}
</style>

<script>
import L from "mapbox.js";
import {
  combine,
  buffer,
  bboxPolygon,
  difference,
  feature,
  featureCollection,
} from "@turf/turf";

const DebugLayer = L.GridLayer.extend({
  createTile: function (coords) {
    const tile = document.createElement("div");
    tile.style.color = "red";
    tile.style.outline = "1px solid red";
    tile.style.fontSize = "14pt";
    tile.innerHTML = [coords.z, coords.x, coords.y].join("/");
    return tile;
  },
});

const MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1Ijoic3RhdGlvbmEiLCJhIjoiY2oyMTMyeGt0MDAwcjMybzczejZtdXpsYSJ9.J-CqUJNj-jPGo0oZp3Owfg";
const DEFAULT_MAP_OPTIONS = {
  boxZoom: false,
  keyboard: false,
  scrollWheelZoom: false,
  touchZoom: false,
  tap: false,
  zoomControl: false,
  attributionControl: false,
  // This prevents the user from panning outside the world's edge
  maxBounds: L.latLngBounds(L.latLng(-90, -200), L.latLng(90, 200)),
  maxBoundsViscosity: 1.0,
  minZoom: 3,
};
const NONINTERACTIVE_MAP_OPTIONS = {
  doubleClickZoom: false,
  interactive: false,
  dragging: false,
};
const INTERACTIVE_MAP_OPTIONS = {
  doubleClickZoom: true,
  interactive: true,
  dragging: true,
};
const DEFAULT_MAP_TYPE = "mapbox.satellite";
const DEFAULT_ICON = L.icon({
  iconUrl: "/img/marker.png",
  iconSize: [28, 28],
  iconAnchor: [0, 0],
});

export default {
  name: "MapCanvas",
  props: {
    geojson: { type: Object },
    mapType: { type: String, default: DEFAULT_MAP_TYPE },
    fill: { type: String },
    fillOpacity: { type: String },
    stroke: { type: String },
    strokeWidth: { type: String },
    debug: { type: Boolean, default: false },
    interactive: { type: Boolean, default: false },
    padding: { type: Array, default: () => [50, 30] },
  },
  data() {
    return {
      leafletMap: undefined,
      markerLayer: undefined,
      geojsonLayer: undefined,
      debugLayer: undefined,
    };
  },
  watch: {
    overlay() {
      this.updateGeoLayer();
    },
    leafletMarkers: {
      handler() {
        this.updateMarkersLayer();
      },
      deep: true,
    },
    debug() {
      this.updateDebugLayer();
    },
  },
  computed: {
    featureCollection() {
      return this.toFeatureCollection(this.geojson);
    },
    multiPolygon() {
      const combined = combine(this.featureCollection);
      return combined.features[0];
    },
    overlay() {
      // Compute an overlay only if there is exactly one Feature to display.
      if (!this.leafletMap || !this.geojson || this.leafletMarkers.length > 1)
        return;
      const bounds = this.leafletMap.getBounds();
      const overlayBbox = buffer(
        bboxPolygon([
          bounds.getSouthWest().lng,
          bounds.getSouthWest().lat,
          bounds.getNorthEast().lng,
          bounds.getNorthEast().lat,
        ]),
        1000,
      );
      const overlay = difference(overlayBbox, this.multiPolygon);
      overlay.properties = {
        stroke: this.stroke,
        fill: this.fill,
        "fill-opacity": this.fillOpacity,
        "stroke-width": this.strokeWidth,
      };
      return overlay;
    },
    leafletMarkers() {
      if (!this.geojson.features) return [];
      return this.geojson.features
        .filter((f) => f.type === "Point")
        .map((f) => {
          const lm = L.marker([f.coordinates[1], f.coordinates[0]], {
            icon: DEFAULT_ICON,
          });
          if (f.andThen) {
            f.andThen(lm);
          }
          return lm;
        });
    },
  },
  methods: {
    createMap() {
      L.mapbox.accessToken = MAPBOX_ACCESS_TOKEN;
      this.leafletMap = L.map(
        this.$refs.mapContainer,
        this.interactive
          ? { ...DEFAULT_MAP_OPTIONS, ...INTERACTIVE_MAP_OPTIONS }
          : { ...DEFAULT_MAP_OPTIONS, ...NONINTERACTIVE_MAP_OPTIONS },
      );
      L.mapbox.tileLayer(this.mapType).addTo(this.leafletMap);
      this.updateLayers();

      if (this.interactive) {
        // This prohibits zooming out to the entire universe
        this.leafletMap.setMinZoom(this.leafletMap.getZoom());
        this.leafletMap.setMaxBounds(this.leafletMap.getBounds());
      }

      /*
       Whenever the map canvas resizes, we want to ensure that the contents
       of the map are loaded to fit the new container size. This requires us to
       call leafletMap.invalidateSize to have the leafletMap reloaded.

       See RFP.vue for an important example on what happens when viewport aspect
       ratio changes, therefore requiring a resize of the MapCanvas.
       */
      this.leafletMap.on("resize", this.updateOnResize);
    },
    updateOnResize() {
      // see this regarding why use a timeout: https://stackoverflow.com/questions/24412325/resizing-a-leaflet-map-on-container-resize
      // Note that the timeout is relevant only for the this.leafletMap.invalidateSize call inside of this.updateLayers();

      /*
       * Here we do this twice, one after 200 milliseconds, one after 500 milliseconds
       * at 200 milliseconds, we see a super quick and smooth resize of the map
       */
      setTimeout(() => {
        this.updateLayers();
      }, 200);
      /*
       * but in the case that the time between the "resize" map event, and the
       * screen resize finishing is greater than 200 ms, trying once again after
       * 500 ms is a catch all to ensure we don't get stuck in a unresized map
       */
      setTimeout(() => {
        this.updateLayers();
      }, 500);
    },
    updateLayers() {
      this.leafletMap.invalidateSize(false);
      this.updateGeoLayer();
      this.updateMarkersLayer();
      this.updateDebugLayer();
    },
    updateMarkersLayer() {
      if (this.markerLayer) {
        this.leafletMap.removeLayer(this.markerLayer);
      }
      this.markerLayer = L.layerGroup();
      this.leafletMarkers.forEach((m) => this.markerLayer.addLayer(m));
      this.markerLayer.addTo(this.leafletMap);
    },
    updateGeoLayer() {
      if (this.geojson) {
        const shape = L.geoJson(this.geojson);
        this.leafletMap.fitBounds(shape.getBounds(), {
          animate: false,
          // NOTE: Adjust as needed
          padding: this.padding,
        });
        if (this.geojsonLayer) {
          this.leafletMap.removeLayer(this.geojsonLayer);
        }
        this.geojsonLayer = L.geoJson(this.overlay, {
          style: L.mapbox.simplestyle.style,
        });
        this.geojsonLayer.addTo(this.leafletMap);
      }
    },
    updateDebugLayer() {
      if (this.debugLayer) {
        this.leafletMap.removeLayer(this.debugLayer);
      }

      if (this.debug) {
        this.debugLayer = new DebugLayer();
        this.debugLayer.addTo(this.leafletMap);
      }
    },
    removeMap() {
      if (this.leafletMap) {
        this.leafletMap.remove();
      }
    },
    toFeature(geojson) {
      switch (geojson.type) {
        case "Feature":
          return feature(geojson.geometry, geojson.properties);
        default:
          return feature(geojson);
      }
    },
    toFeatureCollection(geojson) {
      switch (geojson.type) {
        case "FeatureCollection":
          return featureCollection(geojson.features.map(this.toFeature));
        default:
          return featureCollection([this.toFeature(geojson)]);
      }
    },
  },
  mounted() {
    this.createMap();
  },
  unmounted() {
    this.removeMap();
  },
};
</script>
